/**
 * Portfolio público por empresa.
 *
 * Cada empresa puede publicar un portfolio de sus obras accesible mediante un
 * enlace (p. ej. /portfolio/reformas-hermes). La configuración y los datos se
 * resuelven desde localStorage, ya que la app no tiene backend.
 */
import type { Client, Company, PortfolioConfig, Project, ProjectPhoto } from '../types';

const COMPANIES_KEY = 'reformall_all_companies';
const key = (companyId: string, entity: string) => `reformall_${companyId}_${entity}`;

export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function load<T>(k: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(k);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function save(k: string, value: unknown): void {
  localStorage.setItem(k, JSON.stringify(value));
}

export function getCompanies(): Company[] {
  return load<Company[]>(COMPANIES_KEY, []);
}

export function loadPortfolio(companyId: string): PortfolioConfig | null {
  return load<PortfolioConfig | null>(key(companyId, 'portfolio'), null);
}

export function savePortfolio(config: PortfolioConfig): void {
  save(key(config.companyId, 'portfolio'), config);
}

export function slugExists(slug: string, exceptCompanyId?: string): boolean {
  return getCompanies().some(c => {
    if (c.id === exceptCompanyId) return false;
    return loadPortfolio(c.id)?.slug === slug;
  });
}

export function uniqueSlug(base: string, companyId: string): string {
  let s = slugify(base) || 'empresa';
  if (s.length < 3) s = `empresa-${s}`;
  if (!slugExists(s, companyId)) return s;
  let i = 2;
  while (slugExists(`${s}-${i}`, companyId)) i++;
  return `${s}-${i}`;
}

export function ensurePortfolio(company: Company): PortfolioConfig {
  const existing = loadPortfolio(company.id);
  if (existing) return existing;

  const config: PortfolioConfig = {
    companyId: company.id,
    slug: uniqueSlug(company.name, company.id),
    enabled: false,
    title: company.name,
    subtitle: 'Nuestros trabajos de reforma',
    description: '',
    coverImage: company.logo,
    contactPhone: company.phone,
    contactEmail: company.email,
    contactWhatsapp: company.phone,
    featuredProjectIds: [],
    theme: 'clasico',
    layout: 'grid',
    grouping: 'none',
    updatedAt: new Date().toISOString(),
  };
  savePortfolio(config);
  return config;
}

export function resolvePortfolioBySlug(slug: string): { company: Company; portfolio: PortfolioConfig } | null {
  for (const company of getCompanies()) {
    const portfolio = loadPortfolio(company.id);
    if (portfolio?.slug === slug) return { company, portfolio };
  }
  return null;
}

export function buildPortfolioUrl(slug: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/portfolio/${slug}`;
}

export function getProjects(companyId: string): Project[] {
  return load<Project[]>(key(companyId, 'projects'), []);
}

export function getPhotos(companyId: string): ProjectPhoto[] {
  return load<ProjectPhoto[]>(key(companyId, 'photos'), []);
}

export function getClients(companyId: string): Client[] {
  return load<Client[]>(key(companyId, 'clients'), []);
}

export interface PortfolioWork {
  project: Project;
  photos: ProjectPhoto[];
  clientName?: string;
}

/** Obras que se muestran en el portfolio (destacadas o, si no hay, las que tienen fotos). */
export function resolvePortfolioWorks(companyId: string, portfolio: PortfolioConfig): PortfolioWork[] {
  const projects = getProjects(companyId);
  const photos = getPhotos(companyId);
  const clients = getClients(companyId);

  const chosen = portfolio.featuredProjectIds.length
    ? (portfolio.featuredProjectIds
        .map(id => projects.find(p => p.id === id))
        .filter((p): p is Project => Boolean(p)))
    : projects.filter(p => photos.some(ph => ph.projectId === p.id));

  return chosen.map(project => ({
    project,
    photos: photos.filter(ph => ph.projectId === project.id),
    clientName: clients.find(c => c.id === project.clientId)?.name,
  }));
}
